<script setup>
import { computed, inject, toValue } from 'vue';
import { useField, FormContextKey } from 'vee-validate';

const props = defineProps({
    id: {
        type: String,
        default: undefined
    },
    class: {
        type: String,
        default: null
    },
    // Classes da coluna em volta do campo (o `class` vai para o Dropdown).
    classeColuna: {
        type: String,
        default: 'col-12'
    },
    // Nome do campo no schema do useForm. Quando informado, o componente grava o
    // valor da opção escolhida no formulário e lê o erro sozinho.
    name: {
        type: String,
        default: null
    },
    // Opção selecionada (objeto inteiro), para a view continuar reagindo à escolha.
    modelValue: {
        type: [Object, String, Number, Boolean],
        default: null
    },
    opcoes: {
        type: Array,
        default: () => []
    },
    titulo: {
        type: String,
        default: ''
    },
    help: {
        type: String,
        default: null
    },
    placeholder: {
        type: String,
        default: 'Selecione'
    },
    optionLabel: {
        type: String,
        default: 'nome'
    },
    // Chave da segunda linha da opção (ex.: 'descricao', 'sigla', 'setor_nome').
    descricaoChave: {
        type: String,
        default: null
    },
    // Formata o valor da `descricaoChave` antes de exibir (ex.: formataCpf).
    formatarDescricao: {
        type: Function,
        default: null
    },
    // Chave da opção que vai para o campo do formulário.
    valorChave: {
        type: String,
        default: 'id'
    },
    // Converte a opção escolhida no valor gravado no formulário, quando `valorChave`
    // não basta (ex.: precisar de String(opcao.id) ou de um booleano).
    transformarValor: {
        type: Function,
        default: null
    },
    // Grava a opção inteira no formulário (schema com `object()`); a opção exibida
    // continua sendo encontrada pela `valorChave`.
    valorObjeto: {
        type: Boolean,
        default: false
    },
    // Normalmente dispensável: a obrigatoriedade vem do schema. Serve para forçar o
    // asterisco em regra condicional (`when`), que o schema não expõe estaticamente.
    obrigatorio: {
        type: Boolean,
        default: false
    },
    filtro: {
        type: Boolean,
        default: false
    },
    // Chaves usadas na busca, quando não basta o `optionLabel` (ex.: ['nome', 'cpf']).
    filtroCampos: {
        type: Array,
        default: null
    },
    limpavel: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    carregando: {
        type: Boolean,
        default: false
    },
    // Texto exibido quando não há opções (ex.: lista dependente de outro campo).
    mensagemVazia: {
        type: String,
        default: null
    },
    // Texto exibido quando a busca do filtro não encontra nada.
    mensagemFiltroVazio: {
        type: String,
        default: undefined
    },
    erro: {
        type: String,
        default: null
    },
    // Repassado ao Dropdown do PrimeVue (ex.: fonte menor em cabeçalhos).
    pt: {
        type: Object,
        default: undefined
    },
    // Mesma altura do CampoBuscaLista e dos botões `size="small"`, para alinhar em cabeçalhos.
    small: {
        type: Boolean,
        default: false
    },
    // Desenha só as opções visíveis, para listas com milhares de itens.
    rolagemVirtual: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

// Getter para acompanhar mudanças no nome (ex.: índice de item em useFieldArray após remover outro).
const campoForm = props.name ? useField(() => props.name) : null;

const valorDoFormulario = (opcao) => {
    if (props.transformarValor) return props.transformarValor(opcao);
    if (props.valorObjeto) return opcao ?? null;
    return opcao?.[props.valorChave] ?? null;
};

// Com `valorObjeto`, objetos só se comparam pela chave: String() de qualquer objeto dá "[object Object]".
const chaveComparacao = (valor) => (props.valorObjeto ? valor?.[props.valorChave] : valor);

// Sem v-model, a opção exibida é deduzida do valor que está no formulário: a view só
// precisa de uma ref própria quando faz algo a mais com a opção escolhida.
const selecionado = computed({
    get: () => {
        if (props.modelValue !== null) return props.modelValue;
        if (!campoForm) return null;

        const valor = campoForm.value.value;

        if (valor === null || valor === undefined) return null;

        // Comparação frouxa: o valor pode voltar da API como string e a opção ter id numérico.
        return props.opcoes.find((opcao) =>
            String(chaveComparacao(valorDoFormulario(opcao))) === String(chaveComparacao(valor))
        ) ?? null;
    },
    set: (value) => emit('update:modelValue', value)
});

// Só o valor vai para o formatador: formatadores como formataCpf têm um segundo parâmetro opcional.
const descricaoDaOpcao = (opcao) => {
    const valor = opcao[props.descricaoChave];
    return props.formatarDescricao ? props.formatarDescricao(valor) : valor;
};

const aoMudar = (evento) => {
    if (campoForm) campoForm.handleChange(valorDoFormulario(evento?.value));
    emit('change', evento);
};

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);

const OPCOES_ROLAGEM_VIRTUAL = { itemSize: 38 };

const ESTILO_INPUT_SMALL = { padding: '0.57rem 0.75rem', fontSize: '13px' };

const ptDropdown = computed(() => {
    if (!props.small) return props.pt;
    return { ...props.pt, input: { ...props.pt?.input, style: ESTILO_INPUT_SMALL } };
});

const formulario = inject(FormContextKey, null);

// O asterisco de obrigatório sai da própria regra do schema (ex.: regraDropdown()
// vs regraDropdown({ obrigatorio: false })), para não ser declarado duas vezes.
const obrigatorioDoSchema = computed(() => {
    if (!props.name) return null;

    try {
        const campo = toValue(formulario?.schema)?.describe?.()?.fields?.[props.name];
        return campo ? campo.optional === false : null;
    } catch {
        return null;
    }
});

// O schema só acrescenta o asterisco: a prop continua vencendo, porque um `when`
// aparece como opcional no describe() e ainda assim é obrigatório em tempo de uso.
const obrigatorio = computed(() => props.obrigatorio || obrigatorioDoSchema.value === true);
</script>

<template>
    <div :class="[{ 'field mb-2': props.titulo }, props.classeColuna]">
        <SmallTitle v-if="props.titulo" :titulo="props.titulo" :obrigatorio="obrigatorio" :help="props.help" />
        <Dropdown :id="props.id" v-model="selecionado" :options="props.opcoes" :optionLabel="props.optionLabel"
            :filter="props.filtro" :filterFields="props.filtroCampos" :emptyFilterMessage="props.mensagemFiltroVazio" :showClear="props.limpavel" :disabled="props.disabled" :loading="props.carregando" :virtualScrollerOptions="props.rolagemVirtual ? OPCOES_ROLAGEM_VIRTUAL : undefined"
            :placeholder="props.placeholder" :pt="ptDropdown" :class="[{ 'p-invalid': mensagemErro }, props.class]" @change="aoMudar">
            <template #option="slotProps">
                <slot name="option" v-bind="slotProps">
                    <div class="flex flex-column">
                        <p class="m-0 p-0 text-sm font-regular">{{ slotProps.option[props.optionLabel] }}</p>
                        <span v-if="props.descricaoChave && slotProps.option[props.descricaoChave]" class="text-400" style="font-size: 0.7rem; line-height: 1rem;">
                            {{ descricaoDaOpcao(slotProps.option) }}
                        </span>
                    </div>
                </slot>
            </template>
            <template v-if="$slots.value" #value="slotProps">
                <slot name="value" v-bind="slotProps" />
            </template>
            <template v-if="props.mensagemVazia" #empty>
                <div class="p-3 text-left text-sm text-500">{{ props.mensagemVazia }}</div>
            </template>
        </Dropdown>
        <SmallError v-if="mensagemErro" :error="mensagemErro" />
    </div>
</template>
